import {
  ETAT_HEALTH,
  ETAT_LABEL,
  OPS_PRM,
  POSTES,
  dateLong,
  entityBySync,
  eurK,
  eurKSigned,
  margOf,
  pcm,
  ptxt,
  rateOf,
  type OpsChantier,
  type Poste,
} from '@cockpit/shared';
import { Badge, Drawer, Table, Tabs, Td, Th, cn, type Tone } from '@cockpit/ui';
import { useState } from 'react';
import { useOpsComments } from '../../lib/queries.js';
import { FicheAchats } from './FicheAchats.js';
import { FicheComments } from './FicheComments.js';
import { FicheEcarts } from './FicheEcarts.js';
import { Section, Stat } from './parts.js';

const TONE: Record<string, Tone> = { crit: 'danger', surv: 'warn', bon: 'success' };
const POSTE_LABEL: Record<Poste, string> = {
  ca: 'CA',
  ach: 'Achats',
  st: 'Sous-traitance',
  mo: 'Main-d’œuvre',
  oth: 'Autres charges',
};
const MOIS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];
const monthLabel = (ym: string) => `${MOIS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

/** Effet d'un poste sur la marge : positif quand le CA monte ou qu'un coût baisse. */
const effect = (k: Poste, from: number, to: number) => (k === 'ca' ? to - from : -(to - from));
const tone = (v: number) => (v <= -100 ? 'text-danger-700' : v >= 100 ? 'text-success-700' : 'text-ink-3');
const delta = (v: number) => (Math.abs(v) < 100 ? '—' : eurKSigned(v));

export function FicheDrawer({
  r,
  asOf,
  onClose,
}: {
  r: OpsChantier | null;
  asOf: string;
  onClose: () => void;
}) {
  return (
    <Drawer
      open={!!r}
      onClose={onClose}
      width={980}
      title={r ? r.nom || r.cli || r.code : ''}
      subtitle={r ? <Subtitle r={r} /> : undefined}
    >
      {r && <Fiche key={r.code} r={r} asOf={asOf} />}
    </Drawer>
  );
}

function Subtitle({ r }: { r: OpsChantier }) {
  const c = r.c;
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={TONE[ETAT_HEALTH[c.e]]!} dot>
        {ETAT_LABEL[c.e].replace(/s$/, '')}
      </Badge>
      <Badge tone={c.p ? 'navy' : 'neutral'}>{c.p ? 'Pilotable' : 'En fin de chantier'}</Badge>
      {r.type && <Badge tone="azur">{r.type}</Badge>}
      <span className="font-mono text-[12px]">
        {r.code} · {entityBySync(r.ent)?.label ?? r.ent}
      </span>
    </span>
  );
}

type Tab = 'syn' | 'ecarts' | 'achats' | 'com';

function Fiche({ r, asOf }: { r: OpsChantier; asOf: string }) {
  const [tab, setTab] = useState<Tab>('syn');
  const comments = useOpsComments(r.code);
  return (
    <div className="flex flex-col gap-4">
      <Tabs<Tab>
        label="Sections de la fiche"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'syn', label: 'Synthèse' },
          { key: 'ecarts', label: 'Écarts' },
          { key: 'achats', label: 'Achats' },
          { key: 'com', label: 'Commentaires', count: comments.data?.length },
        ]}
      />
      {tab === 'syn' && <Synthese r={r} asOf={asOf} />}
      {tab === 'ecarts' && <FicheEcarts r={r} />}
      {tab === 'achats' && <FicheAchats r={r} />}
      {tab === 'com' && (
        <FicheComments
          r={r}
          asOf={asOf}
          list={comments.data}
          loading={comments.isLoading}
          error={comments.error as Error | null}
        />
      )}
    </div>
  );
}

function Synthese({ r, asOf }: { r: OpsChantier; asOf: string }) {
  const c = r.c;
  const mP = margOf(r.Pv);
  const mR = margOf(r.Rv);
  const mT = margOf(r.Tm);
  const late = c.fd;
  const planning = late
    ? { v: `+${daysAfter(r.fin!, asOf)} j`, s: `fin prévue dépassée (${dateLong(r.fin)})`, bad: true }
    : r.fin
      ? r.fin >= asOf
        ? { v: `J−${daysAfter(asOf, r.fin)}`, s: `fin prévue le ${dateLong(r.fin)}`, bad: false }
        : { v: 'terminé', s: `fin prévue le ${dateLong(r.fin)}`, bad: false }
      : { v: '—', s: 'pas de date de fin', bad: false };
  const attention = [...r.anoms.filter((a) => a.s !== 'i').map((a) => a.txt), ...r.dates.map((d) => d.txt)];
  let cum = r.fact - r.cX;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13.5px] text-ink-2">
        {r.cli ? (
          <>
            Client <b className="text-ink">{r.cli}</b> ·{' '}
          </>
        ) : null}
        {r.cond ? (
          <>
            Conducteur <b className="text-ink">{r.cond}</b>
          </>
        ) : (
          <span className="text-danger-700">aucun conducteur</span>
        )}
        {r.com ? ` · Commercial ${r.com}` : ''} · {r.deb ? dateLong(r.deb) : '?'} →{' '}
        {r.fin ? dateLong(r.fin) : '?'}
      </p>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-line-soft bg-line-soft lg:grid-cols-5">
        <Stat
          label="CA revu"
          value={eurK(r.CAr)}
          sub={
            r.car && r.car !== r.ca
              ? `commande ${eurK(r.ca)} (${eurKSigned(r.car - r.ca)})`
              : 'identique à la commande'
          }
        />
        <Stat label="Facturé" value={`${r.fa ?? '—'} %`} sub={`${eurK(r.fact)} · reste ${eurK(r.raf)}`} />
        <Stat label="Avancement" value={`${r.cr} %`} sub={`reste à dépenser ${eurK(r.RC)}`} />
        <Stat
          label="Marge prévue → à terminaison"
          value={`${pcm(rateOf(r.Pv))} → ${pcm(rateOf(r.Tm))}`}
          sub={`${eurK(mP)} → ${eurK(mT)} (${eurKSigned(mT - mP)})`}
          bad={c.e === 'r'}
        />
        <Stat label="Planning" value={planning.v} sub={planning.s} bad={planning.bad} />
      </dl>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Pourquoi ce statut">
          <ul className="list-disc pl-5 text-[14px] text-ink">
            {c.why.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <div
            className={cn(
              'rounded-md px-3.5 py-3',
              c.p
                ? c.e === 'r'
                  ? 'bg-danger-50'
                  : c.e === 'a'
                    ? 'bg-warn-50'
                    : 'bg-success-50'
                : 'bg-surface-2',
            )}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-3">
              {c.p ? 'Ce qu’on peut encore faire' : 'Atterrissage'}
            </p>
            {c.todo.map(([t, v]) => (
              <div key={t} className="flex justify-between gap-3 py-0.5 text-[14px]">
                <span>{t}</span>
                <b className="tnum">{v == null ? '' : typeof v === 'string' ? v : eurK(v)}</b>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Points d’attention" aside={attention.length ? `${attention.length}` : undefined}>
          {attention.length ? (
            <ul className="list-disc pl-5 text-[13.5px] text-ink">
              {attention.slice(0, 8).map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[13.5px] text-ink-3">Aucune anomalie ni incohérence de dates.</p>
          )}
        </Section>
      </div>

      <Section
        title="De la marge prévue à la marge à terminaison"
        aside="en vert ce qui améliore la marge, en rouge ce qui la dégrade"
      >
        <Table minWidth={680}>
          <thead>
            <tr>
              <Th>Poste</Th>
              <Th align="right">Prévu</Th>
              <Th align="right">Révisé</Th>
              <Th align="right">Réalisé</Th>
              <Th align="right">Reste</Th>
              <Th align="right">À terminaison</Th>
              <Th align="right">Écart</Th>
            </tr>
          </thead>
          <tbody>
            {POSTES.map((k) => {
              const e2 = effect(k, r.Pv[k], r.Tm[k]);
              return (
                <tr key={k}>
                  <Td className="font-semibold">{POSTE_LABEL[k]}</Td>
                  <Td align="right">{eurK(r.Pv[k])}</Td>
                  <Td align="right">{eurK(r.Rv[k])}</Td>
                  <Td align="right">{eurK(r.Ad[k])}</Td>
                  <Td align="right">{eurK(r.Rf[k])}</Td>
                  <Td align="right">{eurK(r.Tm[k])}</Td>
                  <Td align="right" className={cn('font-semibold', tone(e2))}>
                    {delta(e2)}
                  </Td>
                </tr>
              );
            })}
            <tr className="font-bold">
              <Td>Marge</Td>
              <Td align="right">{eurK(mP)}</Td>
              <Td align="right">{eurK(mR)}</Td>
              <Td align="right">{eurK(r.fact - r.cX)}</Td>
              <Td align="right">{eurK(r.raf - r.RC)}</Td>
              <Td align="right" className={mT < 0 ? 'text-danger-700' : undefined}>
                {eurK(mT)}
              </Td>
              <Td align="right" className={tone(mT - mP)}>
                {eurKSigned(mT - mP)}
              </Td>
            </tr>
          </tbody>
        </Table>
        <p className="text-[12.5px] text-ink-3">
          Écart = effet sur la marge, de la signature à la terminaison. À terminaison = réalisé + reste du
          budget revu, poste par poste ; le réalisé qui dépasse déjà le budget revu est conservé. Taux de
          marge : {pcm(rateOf(r.Pv))} prévu, {pcm(rateOf(r.Rv))} révisé, {pcm(rateOf(r.Tm))} à terminaison (
          {ptxt(c.d)}).
        </p>
      </Section>

      <Section title="Reste à faire" aside={r.P.method}>
        {r.P.months.length || r.P.sd.f || r.P.sd.c ? (
          <Table minWidth={560}>
            <thead>
              <tr>
                <Th>Mois</Th>
                <Th align="right">Facturation</Th>
                <Th align="right">Coûts</Th>
                <Th align="right">Marge du mois</Th>
                <Th align="right">Marge cumulée</Th>
              </tr>
            </thead>
            <tbody>
              <tr className="text-ink-3">
                <Td>Déjà réalisé au {dateLong(asOf)}</Td>
                <Td align="right">{eurK(r.fact)}</Td>
                <Td align="right">{eurK(r.cX)}</Td>
                <Td />
                <Td align="right">{eurK(cum)}</Td>
              </tr>
              {r.P.months.map((m) => {
                cum += m.f - m.c;
                return (
                  <tr key={m.m}>
                    <Td>{monthLabel(m.m)}</Td>
                    <Td align="right">{eurK(m.f)}</Td>
                    <Td align="right">{eurK(m.c)}</Td>
                    <Td align="right" className={m.f - m.c < 0 ? 'text-danger-700' : undefined}>
                      {eurKSigned(m.f - m.c)}
                    </Td>
                    <Td align="right" className="font-bold">
                      {eurK(cum)}
                    </Td>
                  </tr>
                );
              })}
              {(r.P.sd.f > 0 || r.P.sd.c > 0) && (
                <tr>
                  <Td>Sans date</Td>
                  <Td align="right">{eurK(r.P.sd.f)}</Td>
                  <Td align="right">{eurK(r.P.sd.c)}</Td>
                  <Td align="right">{eurKSigned(r.P.sd.f - r.P.sd.c)}</Td>
                  <Td align="right" className="font-bold">
                    {eurK(cum + r.P.sd.f - r.P.sd.c)}
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
        ) : (
          <p className="text-[13.5px] text-ink-3">
            Rien à projeter : le chantier est entièrement facturé et ses budgets sont consommés.
          </p>
        )}
        {r.est && r.fin && r.est > r.fin && (
          <p className="text-[12.5px] text-warn-700">
            Fin estimée au {dateLong(r.est)} au rythme actuel des coûts, contre le {dateLong(r.fin)} prévu
            dans Flex.
          </p>
        )}
        <p className="text-[12.5px] text-ink-3">
          Seuil de {OPS_PRM.pil} % facturé : au-delà, la marge est quasiment figée.
        </p>
      </Section>
    </div>
  );
}

const daysAfter = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
